import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { PriceProvider } from "./context/PriceContext";
import { ShopStatusProvider } from "./context/ShopStatusContext";
import { ErrorProvider } from "./context/ErrorContext";
import ErrorBoundary from "./components/ErrorBoundary";
import FeedbackButton from "./components/FeedbackButton";
import { initializeMemoryMonitoring, logMemoryUsage } from "./utils/memoryOptimization";

// Lazy load pages for better performance
const UploadPage = lazy(() => import("./pages/UploadPage"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const SuccessPage = lazy(() => import("./pages/SuccessPage"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const PrintQueue = lazy(() => import("./pages/PrintQueue"));
const CheckStatus = lazy(() => import("./pages/CheckStatus"));
const AdminPricePanel = lazy(() => import("./pages/AdminPricePanel"));
const AdminCleanup = lazy(() => import("./pages/AdminCleanup"));
const UnifiedAnalytics = lazy(() => import("./pages/UnifiedAnalytics"));
const AdminEnquiries = lazy(() => import("./pages/AdminEnquiries"));

// Lazy load components
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./components/TermsAndConditions"));
const CancellationRefund = lazy(() => import("./components/CancellationRefund"));
const ShippingDelivery = lazy(() => import("./components/ShippingDelivery"));
const ContactUs = lazy(() => import("./components/ContactUs"));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);
 

function PrivateRoute({ children }) {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  if (user === undefined) return null; // or loading spinner
  return user ? children : <Navigate to="/admin-login" />;
}

function App() {
  useEffect(() => {
    // Initialize memory monitoring in development
    initializeMemoryMonitoring();
    logMemoryUsage('App Initialization');
    
    // Cleanup on unmount
    return () => {
      logMemoryUsage('App Cleanup');
    };
  }, []);

  return (
    <ErrorProvider>
      <PriceProvider>
        <ShopStatusProvider>
          <ErrorBoundary>
            <Router>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/" element={<UploadPage />} />
                  <Route path="/payment" element={<PaymentPage />} />
                  <Route path="/success" element={<SuccessPage />} />
                  <Route path="/admin-login" element={<AdminLogin />} />
                  <Route path="/admin-dashboard" element={
                    <PrivateRoute>
                      <AdminDashboard />
                    </PrivateRoute>
                  } />
                  <Route path="/print-queue" element={
                    <PrivateRoute>
                      <PrintQueue />
                    </PrivateRoute>
                  } />
                  <Route path="/check-status" element={<CheckStatus />} />
                  <Route path="/admin-price" element={
                    <PrivateRoute>
                      <AdminPricePanel />
                    </PrivateRoute>
                  } />
                         <Route path="/admin-cleanup" element={
                           <PrivateRoute>
                             <AdminCleanup />
                           </PrivateRoute>
                         } />
                         <Route path="/unified-analytics" element={
                           <PrivateRoute>
                             <UnifiedAnalytics />
                           </PrivateRoute>
                         } />
                         <Route path="/admin-enquiries" element={
                           <PrivateRoute>
                             <AdminEnquiries />
                           </PrivateRoute>
                         } />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                  <Route path="/cancellation-refund" element={<CancellationRefund />} />
                  <Route path="/shipping-delivery" element={<ShippingDelivery />} />
                </Routes>
              </Suspense>
            </Router>
          </ErrorBoundary>
        </ShopStatusProvider>
      </PriceProvider>
    </ErrorProvider>
  );
}

export default App;